import { resources } from "@constants/hardcoded-strings";
import { EntityStatusTypes } from "@enums/entity-status-types";
import { AxnCompany, AxnRPServiceWithSOID, AxnRelyingParty, AxnSelectedServiceOfferingSO, SimpleAxnRpServiceWorkflow, SimpleAxnWorkflow } from "@idw-types/data/reference-data/axn";
import { ApAssertion, Assertion } from "@idw-types/data/reference-data/generic";
import { mapAssertionsToSelectOptions, mapEntitiesWithNameAndIdToSelectOptions, mapRpServicesWithSOIDToSelectOptionsWithStyle, mapWorkflowsToSelectOptionsWithStyle } from "@utils/select-option-mapping-util";

export const getAvailableRpServicesForRelyingPartyIds = (filteredRpServicesWithSOIDs: Array<AxnRPServiceWithSOID>, relyingPartyIds: Array<string>) => {
    return filteredRpServicesWithSOIDs.filter(service => relyingPartyIds.includes(`${service.relyingPartyID}`));
};

export const getAvailableRpServicesForWorkflowIds = (
    simpleAxnRpServiceWorkflows: Array<SimpleAxnRpServiceWorkflow>,
    filteredRpServicesWithSOIDs: Array<AxnRPServiceWithSOID>,
    workflowIds: Array<string>
) => {
    const filteredSimpleAxnRpServiceWorkflows = simpleAxnRpServiceWorkflows.filter(workflowService => workflowIds.includes(workflowService.workflowID));
    const availableServices: Array<AxnRPServiceWithSOID> = [];

    filteredRpServicesWithSOIDs.forEach(service => {
        const workflow = filteredSimpleAxnRpServiceWorkflows.find(workflowService => workflowService.rpAXNServiceID === service.id);
        if (workflow) {
            availableServices.push(service);
        }
    });
    return availableServices;
};


export const getSimpleAxnWorkflowsWithStatus = (simpleAxnWorkflows: Array<SimpleAxnWorkflow>): Array<SimpleAxnWorkflow> => {
    return simpleAxnWorkflows.map(workflow => ({
        ...workflow,
        name: `${workflow.status === EntityStatusTypes.INACTIVE ? "(" + resources.common.archived + ") " : ""}${workflow.name}`
    }));
};

export const getRpServicesWithSOIDAndStatus = (rpServicesWithSOIDs: Array<AxnRPServiceWithSOID>): Array<AxnRPServiceWithSOID> => {
    return rpServicesWithSOIDs.map(service => ({
        ...service,
        serviceName: `${service.status === EntityStatusTypes.INACTIVE ? "(" + resources.common.archived + ") " : ""}${service.serviceName}`
    }));
};

export const getFilteredRpServicesWithSOIDs = (
    includeArchivedServicesAndWorkflows: boolean,
    rpServicesWithSOIDAndStatus: Array<AxnRPServiceWithSOID>
): Array<AxnRPServiceWithSOID> => {
    if (includeArchivedServicesAndWorkflows) {
        return rpServicesWithSOIDAndStatus;
    } else {
        return rpServicesWithSOIDAndStatus.filter(simpleAxnWorkflow => simpleAxnWorkflow.status === EntityStatusTypes.ACTIVE);
    }
};

export const getFilteredSimpleAxnWorkflows = (
    includeArchivedServicesAndWorkflows: boolean,
    simpleAxnWorkflowsWithStatus: Array<SimpleAxnWorkflow>
): Array<SimpleAxnWorkflow> => {
    if (includeArchivedServicesAndWorkflows) {
        return simpleAxnWorkflowsWithStatus;
    } else {
        return simpleAxnWorkflowsWithStatus.filter(simpleAxnWorkflow => simpleAxnWorkflow.status === EntityStatusTypes.ACTIVE);
    }
};

export const getAvailableServiceOfferingIdsFromRPServicesWithSOIDs = (availableRpsWithSOIDs: Array<AxnRPServiceWithSOID>) => {
    const availableServiceOfferingIds: Array<string> = [];
    availableRpsWithSOIDs.forEach(rp => {
        rp.serviceOfferingIDs.forEach(soId => {
            if (!availableServiceOfferingIds.includes(`${soId}`)) {
                availableServiceOfferingIds.push(`${soId}`);
            }
        });
    });
    return availableServiceOfferingIds;
};

export const getAvailableAssertionIdsFromServiceOfferingIds = (apAssertions: Array<ApAssertion>, serviceOfferingIds: Array<string>) => {
    return apAssertions
        .filter(apAssertion => serviceOfferingIds.includes(apAssertion.serviceOfferingID))
        .map(apAssertion => apAssertion.assertionID);
};

export const getAvailableRelyingPartyIdsFromCompanyIds = (relyingParties: Array<AxnRelyingParty>, companyIds: Array<string>) => {
    let availableRelyingParties = relyingParties;
    if (companyIds.length > 0) {
        availableRelyingParties = availableRelyingParties.filter(rp => companyIds.includes(rp.companyID));
    }
    return availableRelyingParties.map(rp => rp.id);
};

const getFilteredSelectedAssertions = (assertions: Array<Assertion>, selectedAssertionIds: Array<string>) => {
    return assertions.filter(assertion => selectedAssertionIds.includes(assertion.id));
};

const getFilteredSelectedServiceOfferingsAndAssertions = (
    axnSelectedServiceOfferingSOs: Array<AxnSelectedServiceOfferingSO>,
    apAssertions: Array<ApAssertion>,
    assertions: Array<Assertion>,
    selectedServiceOfferingIds: Array<string>,
    selectedAssertionIds: Array<string>
) => {
    let filteredSelectedServiceOfferings: Array<AxnSelectedServiceOfferingSO> = [];
    let filteredSelectedAssertions: Array<Assertion> = [];

    if (selectedServiceOfferingIds.length > 0) {
        filteredSelectedServiceOfferings = axnSelectedServiceOfferingSOs.filter(so => selectedServiceOfferingIds.includes(so.id));
        const availableAssertionIds = apAssertions
            .filter(apAssertion => selectedServiceOfferingIds.includes(apAssertion.serviceOfferingID) && selectedAssertionIds.includes(apAssertion.assertionID))
            .map(apAssertion => apAssertion.assertionID);
        filteredSelectedAssertions = getFilteredSelectedAssertions(assertions, availableAssertionIds);
    } else {
        filteredSelectedAssertions = getFilteredSelectedAssertions(assertions, selectedAssertionIds);
    }

    return {
        filteredSelectedServiceOfferings,
        filteredSelectedAssertions
    };
};

const getFilteredSelectedRpServicesServiceOfferingsAndAssertions = (
    filteredRpServicesWithSOIDs: Array<AxnRPServiceWithSOID>,
    axnSelectedServiceOfferingSOs: Array<AxnSelectedServiceOfferingSO>,
    apAssertions: Array<ApAssertion>,
    assertions: Array<Assertion>,
    selectedRpServiceIds: Array<string>,
    selectedServiceOfferingIds: Array<string>,
    selectedAssertionIds: Array<string>
) => {
    let data: {
        filteredSelectedServiceOfferings: Array<AxnSelectedServiceOfferingSO>;
        filteredSelectedAssertions: Array<Assertion>;
    } = {
        filteredSelectedServiceOfferings: [],
        filteredSelectedAssertions: []
    };

    const filteredSelectedRpServices = filteredRpServicesWithSOIDs.filter(service => selectedRpServiceIds.includes(service.id));

    if (filteredSelectedRpServices.length > 0) {
        const availableServiceOfferingIds = getAvailableServiceOfferingIdsFromRPServicesWithSOIDs(filteredSelectedRpServices);
        const filteredSelectedServiceOfferingIds = selectedServiceOfferingIds.filter(soId => availableServiceOfferingIds.includes(soId));

        if (filteredSelectedServiceOfferingIds.length > 0) {
            data = getFilteredSelectedServiceOfferingsAndAssertions(axnSelectedServiceOfferingSOs, apAssertions, assertions, filteredSelectedServiceOfferingIds, selectedAssertionIds);
        } else {
            const availableAssertionIds = getAvailableAssertionIdsFromServiceOfferingIds(apAssertions, availableServiceOfferingIds);
            const filteredSelectedAssertionIds = selectedAssertionIds.filter(assertionId => availableAssertionIds.includes(assertionId));
            data = getFilteredSelectedServiceOfferingsAndAssertions(axnSelectedServiceOfferingSOs, apAssertions, assertions, [], filteredSelectedAssertionIds);
        }
    } else {
        data = getFilteredSelectedServiceOfferingsAndAssertions(axnSelectedServiceOfferingSOs, apAssertions, assertions, selectedServiceOfferingIds, selectedAssertionIds);
    }

    return {
        filteredSelectedRpServices,
        ...data
    };
};

const getFilteredSelectedWorkflowsRpServicesServiceOfferingsAndAssertions = (
    filteredRpServicesWithSOIDs: Array<AxnRPServiceWithSOID>,
    axnSelectedServiceOfferingSOs: Array<AxnSelectedServiceOfferingSO>,
    apAssertions: Array<ApAssertion>,
    assertions: Array<Assertion>,
    filteredSimpleAxnWorkflows: Array<SimpleAxnWorkflow>,
    simpleAxnRpServiceWorkflows: Array<SimpleAxnRpServiceWorkflow>,
    selectedWorkflowIds: Array<string>,
    selectedRpServiceIds: Array<string>,
    selectedServiceOfferingIds: Array<string>,
    selectedAssertionIds: Array<string>
) => {
    let data: {
        filteredSelectedRpServices: Array<AxnRPServiceWithSOID>;
        filteredSelectedServiceOfferings: Array<AxnSelectedServiceOfferingSO>;
        filteredSelectedAssertions: Array<Assertion>;
    } = {
        filteredSelectedRpServices: [],
        filteredSelectedServiceOfferings: [],
        filteredSelectedAssertions: []
    }

    const filteredSelectedWorkflows = filteredSimpleAxnWorkflows.filter(workflow => selectedWorkflowIds.includes(workflow.id));

    if (filteredSelectedWorkflows.length > 0) {
        const availableRpServices = getAvailableRpServicesForWorkflowIds(simpleAxnRpServiceWorkflows, filteredRpServicesWithSOIDs, filteredSelectedWorkflows.map(workflow => workflow.id));
        const availableRpServiceIds = availableRpServices.map(service => service.id);
        const filteredSelectedRpServiceIds = selectedRpServiceIds.filter(rpServiceId => availableRpServiceIds.includes(rpServiceId));

        if (filteredSelectedRpServiceIds.length > 0) {
            data = getFilteredSelectedRpServicesServiceOfferingsAndAssertions(
                filteredRpServicesWithSOIDs,
                axnSelectedServiceOfferingSOs,
                apAssertions,
                assertions,
                filteredSelectedRpServiceIds,
                selectedServiceOfferingIds,
                selectedAssertionIds
            );
        } else {
            const availableServiceOfferingIds = getAvailableServiceOfferingIdsFromRPServicesWithSOIDs(availableRpServices);
            const filteredSelectedServiceOfferingIds = selectedServiceOfferingIds.filter(soId => availableServiceOfferingIds.includes(soId));

            if (filteredSelectedServiceOfferingIds.length > 0) {
                data = getFilteredSelectedRpServicesServiceOfferingsAndAssertions(
                    filteredRpServicesWithSOIDs,
                    axnSelectedServiceOfferingSOs,
                    apAssertions,
                    assertions,
                    [],
                    filteredSelectedServiceOfferingIds,
                    selectedAssertionIds
                );
            } else {
                const availableAssertionIds = getAvailableAssertionIdsFromServiceOfferingIds(apAssertions, availableServiceOfferingIds);
                const filteredSelectedAssertionIds = selectedAssertionIds.filter(assertionId => availableAssertionIds.includes(assertionId));
                data = getFilteredSelectedRpServicesServiceOfferingsAndAssertions(
                    filteredRpServicesWithSOIDs,
                    axnSelectedServiceOfferingSOs,
                    apAssertions,
                    assertions,
                    [],
                    [],
                    filteredSelectedAssertionIds
                );
            }
        }
    } else {
        data = getFilteredSelectedRpServicesServiceOfferingsAndAssertions(
            filteredRpServicesWithSOIDs,
            axnSelectedServiceOfferingSOs,
            apAssertions,
            assertions,
            selectedRpServiceIds,
            selectedServiceOfferingIds,
            selectedAssertionIds
        );
    }

    return {
        filteredSelectedWorkflows,
        ...data
    };
};

const getFilteredSelectedRelyingPartiesWorkflowsRpServicesServiceOfferingsAndAssertions = (
    filteredRpServicesWithSOIDs: Array<AxnRPServiceWithSOID>,
    axnSelectedServiceOfferingSOs: Array<AxnSelectedServiceOfferingSO>,
    apAssertions: Array<ApAssertion>,
    assertions: Array<Assertion>,
    filteredSimpleAxnWorkflows: Array<SimpleAxnWorkflow>,
    simpleAxnRpServiceWorkflows: Array<SimpleAxnRpServiceWorkflow>,
    relyingParties: Array<AxnRelyingParty>,
    selectedRelyingPartyIds: Array<string>,
    selectedWorkflowIds: Array<string>,
    selectedRpServiceIds: Array<string>,
    selectedServiceOfferingIds: Array<string>,
    selectedAssertionIds: Array<string>
) => {
    let data: {
        filteredSelectedWorkflows: Array<SimpleAxnWorkflow>;
        filteredSelectedRpServices: Array<AxnRPServiceWithSOID>;
        filteredSelectedServiceOfferings: Array<AxnSelectedServiceOfferingSO>;
        filteredSelectedAssertions: Array<Assertion>;
    } = {
        filteredSelectedWorkflows: [],
        filteredSelectedRpServices: [],
        filteredSelectedServiceOfferings: [],
        filteredSelectedAssertions: []
    };

    const filteredSelectedRelyingParties = relyingParties.filter(rp => selectedRelyingPartyIds.includes(rp.id));

    if (selectedRelyingPartyIds.length > 0) {
        const availableWorkflows = filteredSimpleAxnWorkflows.filter(workflow => selectedRelyingPartyIds.includes(workflow.relyingPartyID));
        const availableWorkflowIds = availableWorkflows.map(workflow => workflow.id);
        const filteredSelectedWorkflowIds = selectedWorkflowIds.filter(workflowId => availableWorkflowIds.includes(workflowId));

        if (filteredSelectedWorkflowIds.length > 0) {
            data = getFilteredSelectedWorkflowsRpServicesServiceOfferingsAndAssertions(
                filteredRpServicesWithSOIDs,
                axnSelectedServiceOfferingSOs,
                apAssertions,
                assertions,
                filteredSimpleAxnWorkflows,
                simpleAxnRpServiceWorkflows,
                filteredSelectedWorkflowIds,
                selectedRpServiceIds,
                selectedServiceOfferingIds,
                selectedAssertionIds
            );
        } else {
            const availableRpServices = getAvailableRpServicesForRelyingPartyIds(filteredRpServicesWithSOIDs, selectedRelyingPartyIds);
            const availableRpServiceIds = availableRpServices.map(rpService => rpService.id);
            const filteredSelectedRpServiceIds = selectedRpServiceIds.filter(serviceId => availableRpServiceIds.includes(serviceId));

            if (filteredSelectedRpServiceIds.length > 0) {
                data = getFilteredSelectedWorkflowsRpServicesServiceOfferingsAndAssertions(
                    filteredRpServicesWithSOIDs,
                    axnSelectedServiceOfferingSOs,
                    apAssertions,
                    assertions,
                    filteredSimpleAxnWorkflows,
                    simpleAxnRpServiceWorkflows,
                    [],
                    filteredSelectedRpServiceIds,
                    selectedServiceOfferingIds,
                    selectedAssertionIds
                );
            } else {
                const availableServiceOfferingIds = getAvailableServiceOfferingIdsFromRPServicesWithSOIDs(availableRpServices);
                const filteredSelectedServiceOfferingIds = selectedServiceOfferingIds.filter(soId => availableServiceOfferingIds.includes(soId));

                if (filteredSelectedServiceOfferingIds.length > 0) {
                    data = getFilteredSelectedWorkflowsRpServicesServiceOfferingsAndAssertions(
                        filteredRpServicesWithSOIDs,
                        axnSelectedServiceOfferingSOs,
                        apAssertions,
                        assertions,
                        filteredSimpleAxnWorkflows,
                        simpleAxnRpServiceWorkflows,
                        [],
                        [],
                        filteredSelectedServiceOfferingIds,
                        selectedAssertionIds
                    );
                } else {
                    const availableAssertionIds = getAvailableAssertionIdsFromServiceOfferingIds(apAssertions, availableServiceOfferingIds);
                    const filteredSelectedAssertionIds = selectedAssertionIds.filter(assertionId => availableAssertionIds.includes(assertionId));

                    data = getFilteredSelectedWorkflowsRpServicesServiceOfferingsAndAssertions(
                        filteredRpServicesWithSOIDs,
                        axnSelectedServiceOfferingSOs,
                        apAssertions,
                        assertions,
                        filteredSimpleAxnWorkflows,
                        simpleAxnRpServiceWorkflows,
                        [],
                        [],
                        [],
                        filteredSelectedAssertionIds
                    );
                }
            }
        }
    } else {
        data = getFilteredSelectedWorkflowsRpServicesServiceOfferingsAndAssertions(
            filteredRpServicesWithSOIDs,
            axnSelectedServiceOfferingSOs,
            apAssertions,
            assertions,
            filteredSimpleAxnWorkflows,
            simpleAxnRpServiceWorkflows,
            selectedWorkflowIds,
            selectedRpServiceIds,
            selectedServiceOfferingIds,
            selectedAssertionIds
        );
    }

    return {
        filteredSelectedRelyingParties,
        ...data
    };
};

const getFilteredSelectedCompaniesRelyingPartiesWorkflowsRpServicesServiceOfferingsAndAssertions = (
    filteredRpServicesWithSOIDs: Array<AxnRPServiceWithSOID>,
    axnSelectedServiceOfferingSOs: Array<AxnSelectedServiceOfferingSO>,
    apAssertions: Array<ApAssertion>,
    assertions: Array<Assertion>,
    filteredSimpleAxnWorkflows: Array<SimpleAxnWorkflow>,
    simpleAxnRpServiceWorkflows: Array<SimpleAxnRpServiceWorkflow>,
    relyingParties: Array<AxnRelyingParty>,
    companies: Array<AxnCompany>,
    selectedCompanyIds: Array<string>,
    selectedRelyingPartyIds: Array<string>,
    selectedWorkflowIds: Array<string>,
    selectedRpServiceIds: Array<string>,
    selectedServiceOfferingIds: Array<string>,
    selectedAssertionIds: Array<string>
) => {
    let data: {
        filteredSelectedRelyingParties: Array<AxnRelyingParty>;
        filteredSelectedWorkflows: Array<SimpleAxnWorkflow>;
        filteredSelectedRpServices: Array<AxnRPServiceWithSOID>;
        filteredSelectedServiceOfferings: Array<AxnSelectedServiceOfferingSO>;
        filteredSelectedAssertions: Array<Assertion>;
    } = {
        filteredSelectedRelyingParties: [],
        filteredSelectedWorkflows: [],
        filteredSelectedRpServices: [],
        filteredSelectedServiceOfferings: [],
        filteredSelectedAssertions: []
    };

    const filteredSelectedCompanies = companies.filter(company => selectedCompanyIds.includes(company.id));

    if (filteredSelectedCompanies.length > 0) {
        const availableRelyingParties = relyingParties.filter(rp => selectedCompanyIds.includes(rp.companyID));
        const availableRelyingPartyIds = availableRelyingParties.map(rp => rp.id);
        const filteredSelectedRelyingPartyIds = selectedRelyingPartyIds.filter(rpId => availableRelyingPartyIds.includes(rpId));

        if (filteredSelectedRelyingPartyIds.length > 0) {
            data = getFilteredSelectedRelyingPartiesWorkflowsRpServicesServiceOfferingsAndAssertions(
                filteredRpServicesWithSOIDs,
                axnSelectedServiceOfferingSOs,
                apAssertions,
                assertions,
                filteredSimpleAxnWorkflows,
                simpleAxnRpServiceWorkflows,
                relyingParties,
                filteredSelectedRelyingPartyIds,
                selectedWorkflowIds,
                selectedRpServiceIds,
                selectedServiceOfferingIds,
                selectedAssertionIds
            );
        } else {
            //no rps, go to workflow
            const availableWorkflows = filteredSimpleAxnWorkflows.filter(workflow => selectedCompanyIds.includes(workflow.companyID));
            const availableWorkflowIds = availableWorkflows.map(workflow => workflow.id);
            const filteredSelectedWorkflowIds = selectedWorkflowIds.filter(workflowId => availableWorkflowIds.includes(workflowId));

            if (filteredSelectedWorkflowIds.length > 0) {
                data = getFilteredSelectedRelyingPartiesWorkflowsRpServicesServiceOfferingsAndAssertions(
                    filteredRpServicesWithSOIDs,
                    axnSelectedServiceOfferingSOs,
                    apAssertions,
                    assertions,
                    filteredSimpleAxnWorkflows,
                    simpleAxnRpServiceWorkflows,
                    relyingParties,
                    [],
                    filteredSelectedWorkflowIds,
                    selectedRpServiceIds,
                    selectedServiceOfferingIds,
                    selectedAssertionIds
                );
            } else {
                //no workflows, go to service
                const availableRpServices = getAvailableRpServicesForRelyingPartyIds(filteredRpServicesWithSOIDs, availableRelyingPartyIds);
                const availableRpServiceIds = availableRpServices.map(rpService => rpService.id);
                const filteredSelectedRpServiceIds = selectedRpServiceIds.filter(serviceId => availableRpServiceIds.includes(serviceId));

                if (filteredSelectedRpServiceIds.length > 0) {
                    data = getFilteredSelectedRelyingPartiesWorkflowsRpServicesServiceOfferingsAndAssertions(
                        filteredRpServicesWithSOIDs,
                        axnSelectedServiceOfferingSOs,
                        apAssertions,
                        assertions,
                        filteredSimpleAxnWorkflows,
                        simpleAxnRpServiceWorkflows,
                        relyingParties,
                        [],
                        [],
                        filteredSelectedRpServiceIds,
                        selectedServiceOfferingIds,
                        selectedAssertionIds
                    );
                } else {
                    const availableServiceOfferingIds = getAvailableServiceOfferingIdsFromRPServicesWithSOIDs(availableRpServices);
                    const filteredSelectedServiceOfferingIds = selectedServiceOfferingIds.filter(soId => availableServiceOfferingIds.includes(soId));

                    if (filteredSelectedServiceOfferingIds.length > 0) {
                        data = getFilteredSelectedRelyingPartiesWorkflowsRpServicesServiceOfferingsAndAssertions(
                            filteredRpServicesWithSOIDs,
                            axnSelectedServiceOfferingSOs,
                            apAssertions,
                            assertions,
                            filteredSimpleAxnWorkflows,
                            simpleAxnRpServiceWorkflows,
                            relyingParties,
                            [],
                            [],
                            [],
                            filteredSelectedServiceOfferingIds,
                            selectedAssertionIds
                        );
                    } else {
                        const availableAssertionIds = getAvailableAssertionIdsFromServiceOfferingIds(apAssertions, availableServiceOfferingIds);
                        const filteredSelectedAssertionIds = selectedAssertionIds.filter(assertionId => availableAssertionIds.includes(assertionId));

                        data = getFilteredSelectedRelyingPartiesWorkflowsRpServicesServiceOfferingsAndAssertions(
                            filteredRpServicesWithSOIDs,
                            axnSelectedServiceOfferingSOs,
                            apAssertions,
                            assertions,
                            filteredSimpleAxnWorkflows,
                            simpleAxnRpServiceWorkflows,
                            relyingParties,
                            [],
                            [],
                            [],
                            [],
                            filteredSelectedAssertionIds
                        );
                    }
                }
            }
        }
    } else {
        data = getFilteredSelectedRelyingPartiesWorkflowsRpServicesServiceOfferingsAndAssertions(
            filteredRpServicesWithSOIDs,
            axnSelectedServiceOfferingSOs,
            apAssertions,
            assertions,
            filteredSimpleAxnWorkflows,
            simpleAxnRpServiceWorkflows,
            relyingParties,
            selectedRelyingPartyIds,
            selectedWorkflowIds,
            selectedRpServiceIds,
            selectedServiceOfferingIds,
            selectedAssertionIds
        );
    }

    return {
        filteredSelectedCompanies,
        ...data
    };
};


export const getFilteredSelectedData = (
    filteredRpServicesWithSOIDs: Array<AxnRPServiceWithSOID>,
    axnSelectedServiceOfferingSOs: Array<AxnSelectedServiceOfferingSO>,
    apAssertions: Array<ApAssertion>,
    assertions: Array<Assertion>,
    filteredSimpleAxnWorkflows: Array<SimpleAxnWorkflow>,
    simpleAxnRpServiceWorkflows: Array<SimpleAxnRpServiceWorkflow>,
    relyingParties: Array<AxnRelyingParty>,
    companies: Array<AxnCompany>,
    selectedCompanyIds: Array<string>,
    selectedRelyingPartyIds: Array<string>,
    selectedWorkflowIds: Array<string>,
    selectedRpServiceIds: Array<string>,
    selectedServiceOfferingIds: Array<string>,
    selectedAssertionIds: Array<string>
) => {
    let data: {
        filteredSelectedCompanies: Array<AxnCompany>;
        filteredSelectedRelyingParties: Array<AxnRelyingParty>;
        filteredSelectedWorkflows: Array<SimpleAxnWorkflow>;
        filteredSelectedRpServices: Array<AxnRPServiceWithSOID>;
        filteredSelectedServiceOfferings: Array<AxnSelectedServiceOfferingSO>;
        filteredSelectedAssertions: Array<Assertion>;
    } = {
        filteredSelectedCompanies: [],
        filteredSelectedRelyingParties: [],
        filteredSelectedWorkflows: [],
        filteredSelectedRpServices: [],
        filteredSelectedServiceOfferings: [],
        filteredSelectedAssertions: []
    }

    if (selectedCompanyIds.length > 0) {
        data = {
            ...data,
            ...getFilteredSelectedCompaniesRelyingPartiesWorkflowsRpServicesServiceOfferingsAndAssertions(
                filteredRpServicesWithSOIDs,
                axnSelectedServiceOfferingSOs,
                apAssertions,
                assertions,
                filteredSimpleAxnWorkflows,
                simpleAxnRpServiceWorkflows,
                relyingParties,
                companies,
                selectedCompanyIds,
                selectedRelyingPartyIds,
                selectedWorkflowIds,
                selectedRpServiceIds,
                selectedServiceOfferingIds,
                selectedAssertionIds
            )
        }
    } else if (selectedRelyingPartyIds.length > 0) {
        data = {
            ...data,
            ...getFilteredSelectedRelyingPartiesWorkflowsRpServicesServiceOfferingsAndAssertions(
                filteredRpServicesWithSOIDs,
                axnSelectedServiceOfferingSOs,
                apAssertions,
                assertions,
                filteredSimpleAxnWorkflows,
                simpleAxnRpServiceWorkflows,
                relyingParties,
                selectedRelyingPartyIds,
                selectedWorkflowIds,
                selectedRpServiceIds,
                selectedServiceOfferingIds,
                selectedAssertionIds
            )
        };
    } else if (selectedWorkflowIds.length > 0) {
        data = {
            ...data,
            ...getFilteredSelectedWorkflowsRpServicesServiceOfferingsAndAssertions(
                filteredRpServicesWithSOIDs,
                axnSelectedServiceOfferingSOs,
                apAssertions,
                assertions,
                filteredSimpleAxnWorkflows,
                simpleAxnRpServiceWorkflows,
                selectedWorkflowIds,
                selectedRpServiceIds,
                selectedServiceOfferingIds,
                selectedAssertionIds
            )
        };
    } else if (selectedRpServiceIds.length > 0) {
        data = {
            ...data,
            ...getFilteredSelectedRpServicesServiceOfferingsAndAssertions(
                filteredRpServicesWithSOIDs,
                axnSelectedServiceOfferingSOs,
                apAssertions,
                assertions,
                selectedRpServiceIds,
                selectedServiceOfferingIds,
                selectedAssertionIds
            )
        };
    } else if (selectedServiceOfferingIds.length > 0) {
        data = {
            ...data,
            ...getFilteredSelectedServiceOfferingsAndAssertions(
                axnSelectedServiceOfferingSOs,
                apAssertions,
                assertions,
                selectedServiceOfferingIds,
                selectedAssertionIds
            )
        };
    } else if (selectedAssertionIds.length > 0) {
        data = {
            ...data,
            filteredSelectedAssertions: getFilteredSelectedAssertions(assertions, selectedAssertionIds)
        };
    }

    return {
        filteredSelectedCompanyOptions: mapEntitiesWithNameAndIdToSelectOptions(data.filteredSelectedCompanies),
        filteredSelectedRelyingPartyOptions: mapEntitiesWithNameAndIdToSelectOptions(data.filteredSelectedRelyingParties),
        filteredSelectedWorkflowOptions: mapWorkflowsToSelectOptionsWithStyle(data.filteredSelectedWorkflows),
        filteredSelectedRpServiceOptions: mapRpServicesWithSOIDToSelectOptionsWithStyle(data.filteredSelectedRpServices),
        filteredSelectedServiceOfferingOptions: mapEntitiesWithNameAndIdToSelectOptions(data.filteredSelectedServiceOfferings),
        filteredSelectedAssertionOptions: mapAssertionsToSelectOptions(data.filteredSelectedAssertions)
    }
};
